// Section 2 — Per-partner storefront
// Public route: /shop/partner/:slug
// Shows the partner's logo, location (when present), and the catalog of
// their APPROVED products. Customers can add items to the cart with full
// variant attribution → carries through to checkout, Square, and admin
// dashboards.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Globe, Loader2, AlertTriangle, ShoppingBag } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { addToCart } from "@/lib/cart";
import SafeImage from "@/components/SafeImage";
import { ProductImg } from "@/lib/productImage";

export default function PartnerStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [addedSlug, setAddedSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/storefront/partners/${slug}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onAdd = (prod) => {
    addToCart({
      slug: prod.handle || prod.id,
      title: prod.title,
      price_cents: prod.price_cents || Math.round((prod.price || 0) * 100),
      media: prod.images || (prod.image ? [prod.image] : []),
      partner_slug: data?.partner?.slug,
      partner_id: data?.partner?.id,
      source_type: "partner",
    });
    setAddedSlug(prod.handle || prod.id);
    setTimeout(() => setAddedSlug(null), 1500);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-lifted-dim text-sm flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading partner shop…
        </div>
      </div>
    );
  }

  if (err || !data?.partner) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div
          className="glass-panel p-6 border-rose-500/30 text-rose-200 text-sm flex items-center gap-2"
          data-testid="partner-storefront-error"
        >
          <AlertTriangle size={14} />
          {err || "Partner not found or not yet approved."}
        </div>
        <Link to="/shop" className="btn-ghost mt-4 inline-flex">Back to shop</Link>
      </div>
    );
  }

  const p = data.partner;
  const products = data.products || [];
  const locations = data.locations || [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid={`partner-storefront-${p.slug}`}>
      <header className="glass-panel p-6 mb-8 flex flex-col sm:flex-row items-start gap-5">
        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg bg-white/5 grid place-items-center overflow-hidden shrink-0">
          {p.logo_url ? (
            <SafeImage src={p.logo_url} alt={p.business_name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-lifted-dim text-[10px] font-bebas tracking-[0.2em] uppercase">No logo</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-lifted-green">{p.partner_type === "brand" ? "Partner Brand" : p.partner_type === "company" ? "Partner Company" : "Partner Store"}</div>
          <h1 className="font-anton text-3xl sm:text-4xl uppercase text-white" data-testid="partner-storefront-name">
            {p.business_name}
          </h1>
          {p.bio && <p className="text-sm text-lifted-dim mt-2 max-w-2xl">{p.bio}</p>}
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-lifted-dim">
            {(p.primary_address || p.city) && (
              <span className="inline-flex items-center gap-1" data-testid="partner-address">
                <MapPin size={12} />
                {[p.primary_address, p.city, p.state].filter(Boolean).join(", ")}
              </span>
            )}
            {p.website && (
              <a
                href={p.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-lifted-green"
                data-testid="partner-website"
              >
                <Globe size={12} /> Visit site
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Location map placeholder (Section 4 wires live map; here we surface
         the primary location lat/lng + label) */}
      {(locations.length > 0 || p.lat) && (
        <section className="glass-panel p-4 mb-8" data-testid="partner-locations">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2 mb-2">
            <MapPin className="h-3 w-3" /> Where to find us
          </div>
          <ul className="text-xs text-white space-y-1">
            {locations.length > 0 ? locations.map((loc) => (
              <li key={loc.id} className="font-mono">
                · <span className="text-lifted-green">{loc.label}</span> — {loc.address || "(address on file)"}
              </li>
            )) : (
              <li className="font-mono">· Primary location · {p.primary_address || `${p.city}, ${p.state}`}</li>
            )}
          </ul>
        </section>
      )}

      <section data-testid="partner-products">
        <header className="mb-4 flex items-end justify-between">
          <div>
            <div className="eyebrow text-lifted-green">Catalog</div>
            <h2 className="font-anton text-2xl uppercase text-white">Shop {p.business_name}</h2>
          </div>
          <div className="text-xs text-lifted-dim font-mono" data-testid="partner-product-count">
            {products.length} product{products.length === 1 ? "" : "s"}
          </div>
        </header>
        {products.length === 0 ? (
          <div className="glass-panel p-8 text-center text-lifted-dim text-sm" data-testid="partner-products-empty">
            This partner hasn't published any products yet. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((prod) => {
              const handle = prod.handle || prod.id;
              const priceCents = prod.price_cents ?? Math.round((prod.price || 0) * 100);
              return (
                <div
                  key={handle}
                  className="glass-panel p-3 flex flex-col"
                  data-testid={`partner-product-${handle}`}
                >
                  <Link to={`/products/${handle}`} className="block">
                    <div className="aspect-square rounded-md bg-white/5 mb-3 overflow-hidden grid place-items-center">
                      <ProductImg product={prod} alt={prod.title} className="w-full h-full object-cover" data-testid={`partner-product-${handle}-img`} />
                    </div>
                    <div className="font-anton uppercase text-sm text-white truncate">{prod.title}</div>
                    <div className="text-xs text-lifted-green font-mono">
                      ${(priceCents / 100).toFixed(2)}
                    </div>
                  </Link>
                  <button
                    type="button"
                    disabled={!priceCents}
                    onClick={() => onAdd(prod)}
                    className="btn-lifted mt-3 text-xs disabled:opacity-50"
                    data-testid={`partner-add-${handle}`}
                  >
                    <ShoppingBag className="inline mr-1" size={11} />
                    {addedSlug === handle ? "Added!" : "Add to cart"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
